import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import { Box, ButtonBase, LinearProgress, Stack, Typography } from '@mui/material';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import PhoneFrame from '../components/PhoneFrame';
import ProgressRing from '../components/ProgressRing';
import {
    completedLessonCount, coursePercent, getCurrentAssignment, lessonPercent, mockCourses, StudentLesson,
} from '../mockData';
import { BLUE, GREEN, INTER, NAVY, POPPINS, RING_TRACK, SLATE, SUCCESS_TEXT } from '../studentStyles';

function LessonRow({ lesson, isCurrent, onOpen }: { lesson: StudentLesson; isCurrent: boolean; onOpen: () => void }) {
    const percent = lessonPercent(lesson);
    const isComplete = percent === 100;
    const notStarted = percent === 0;

    return (
        <ButtonBase
            onClick={onOpen}
            sx={{
                backgroundColor: isComplete ? `${GREEN}0F` : '#fff',
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                borderLeft: isCurrent ? `4px solid ${BLUE}` : '4px solid transparent',
                p: 2,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                textAlign: 'left',
                transition: 'box-shadow 150ms ease',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
            }}
        >
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 15, color: notStarted ? SLATE : NAVY }}>
                    {lesson.name}
                </Typography>
                <Typography sx={{ fontFamily: INTER, fontSize: 13, color: isComplete ? SUCCESS_TEXT : SLATE, mt: 0.25 }}>
                    {isComplete
                        ? 'Complete — nice work!'
                        : notStarted
                            ? `${lesson.totalPrompts} exercises`
                            : `${lesson.completedPrompts} of ${lesson.totalPrompts} exercises done`}
                </Typography>
                {isCurrent && (
                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 13, color: BLUE, mt: 0.75 }}>
                        Continue
                    </Typography>
                )}
            </Box>
            <ProgressRing percent={percent} size={32} thickness={5} />
        </ButtonBase>
    );
}

export default function StudentCourseDetail() {
    const history = useHistory();
    const { courseId } = useParams() as { courseId: string };

    const course = mockCourses.find(c => c.id === Number(courseId));
    if (!course) {
        return <Redirect to="/mockup/student" />;
    }

    const current = getCurrentAssignment();
    const completed = completedLessonCount(course);
    const percent = coursePercent(course);
    const allDone = completed === course.lessons.length;

    return (
        <PhoneFrame title={course.name} onBack={() => history.push('/mockup/student')}>
            <Stack spacing={3}>

                {/* Course summary */}
                <Box>
                    <Typography sx={{ fontFamily: INTER, fontSize: 14, color: SLATE, mb: 1.5 }}>
                        {course.subtitle}
                    </Typography>
                    {allDone ? (
                        <Box sx={{
                            display: 'inline-flex', alignItems: 'center', gap: 0.75,
                            backgroundColor: `${GREEN}1A`, borderRadius: '999px', px: 1.5, py: 0.75,
                        }}>
                            <CelebrationRoundedIcon sx={{ color: GREEN, fontSize: 18 }} />
                            <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SUCCESS_TEXT }}>
                                Course complete
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Typography sx={{ fontFamily: INTER, fontWeight: 600, fontSize: 13, color: SLATE, mb: 0.75 }}>
                                {completed} of {course.lessons.length} lessons · {percent}%
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={percent}
                                sx={{
                                    height: 8,
                                    borderRadius: '999px',
                                    backgroundColor: RING_TRACK,
                                    '& .MuiLinearProgress-bar': { borderRadius: '999px', backgroundColor: BLUE },
                                }}
                            />
                        </>
                    )}
                </Box>

                {/* Lesson list */}
                <Stack spacing={1.5}>
                    {course.lessons.map(lesson => (
                        <LessonRow
                            key={lesson.id}
                            lesson={lesson}
                            isCurrent={current != null && current.lesson.id === lesson.id}
                            onOpen={() => history.push(`/mockup/student/course/${course.id}/lesson/${lesson.id}`)}
                        />
                    ))}
                </Stack>

            </Stack>
        </PhoneFrame>
    );
}
